import type {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {hasLShapeFootprint} from 'components/customer/Preview3DCommon/helpers/hasLShapeFootprint';

export interface Preview3DComponentProps<T> {
    previewLocation: PreviewLocation;
    expanded?: boolean;
    fullScreen?: boolean;
    hiddenForScreenshot?: boolean;
    raw?: boolean;
    isStandalonePreview?: boolean;
    product?: PreviewFormValues;
    productDataStore?: T;
    isMiniBrowser?: boolean;
    adjustableLegsQuantity?: number;
    link3DRP?: string;
}

export type OperationGroup = {
    ID: string;
    JobID: string;
    PartID: string;
    Operations: Operation[];
    IsShelf?: string;
    IsShelfVertical?: string;
    MfgOrientationID: string;
    IsRightDoor?: boolean;
    IsPairDoor?: boolean;
    IsLeftDoor?: boolean;
    IsDrawerFront?: boolean;
    IsBinWithInnerDrawer?: boolean;
    IsDoorPullout?: boolean;
    IsDrawerBottom?: boolean;
    IsDrawerBack?: boolean;
    IsDrawerRunner?: boolean;
    HideSgement?: boolean;
    ExteriorMaterial?: boolean;
    CarcaseMaterial?: boolean;
    IsShelfPartition?: boolean;
    IsTopHangDoor?: boolean;
    IsBifoldLeftDoor?: boolean;
    IsBifoldRightDoor?: boolean;
    IsRightLShape?: boolean;
    ShelfLabel?: string;
    IsCornerLeftDoor?: boolean;
    IsCornerRightDoor?: boolean;
    NoEdging?: boolean;
    FlipY?: boolean;
    WithEdging?: boolean;
    FlipSubstrate?: boolean;
    IsLeftApplianceMainDoor?: boolean;
    IsLeftApplianceExtendedDoor?: boolean;
    IsRightApplianceMainDoor?: boolean;
    IsRightApplianceExtendedDoor?: boolean;
    IsFrontRangehoodVent?: boolean;
    IsLeftBifoldExtension?: boolean;
    WithTopEdging?: boolean;
    WithFrontEdging?: boolean;
    DynamicExteriorEdging?: boolean;
    AnimationDelay?: string;
    NoEdge?: boolean;
    WithBackEdging?: boolean;
    RotationY?: string;
    Index?: string;
    IsCornerLeft?: boolean;
    IsCornerRight?: boolean;
    IsCorFoldKitRightDoor?: boolean;
    IsCorFoldKitLeftDoor?: boolean;
    AppliedDoorShift?: {
        perimAlongLeg?: number;
        insertDepth?: number;
        alongLeg?: number;
    };
};

export type Operation = {
    Type:
        'panel' | 'perimeter_shape' | 'multihole' | 'system32' | 'drilled_hole';
    ProgName: string;
    Rotation: number;
    Insert: Dimension;
    Segments: Segment[];
    Circle: Circle;
    Repeat: number;
    Pitch?: Dimension;
};

export type Circle = {
    Center?: Dimension;
    Diameter?: number;
    Normal: Dimension;
};

export type Dimension = {
    x?: number;
    y?: number;
    z?: number;
};

export type Coordinate = Dimension & {
    w?: number;
};

export type Segment = {
    EndCoor: Coordinate;
    EndNormal: Dimension;
    EndOrgDisplacement: number;
    StartCoor: Coordinate;
    StartNormal: Dimension;
    StartOrgDisplacement: number;
    OpenBaseCutSegment?: number;
};

export type PreviewPosition =
    | 'LEFT'
    | 'RIGHT'
    | 'RIGHT_BACK'
    | 'LEFT_BACK'
    | 'LEFT_LEVELED'
    | 'FRONT'
    | 'RIGHT_LEVELED'
    | 'BACK'
    | 'TOP'
    | 'LEFT_LEVELED_STATIC'
    | 'TOP_WITHOUT_EDITING'
    | 'PDF_VIEW';

export type ComplexShelfOperation = {
    insert: number;
    openBaseCutSegments: DynamicSegments[];
};

export type DynamicSegments = {
    first: Partial<Segment>;
    second: Partial<Segment>;
    third: Partial<Segment>;
    fourth: Partial<Segment>;
};

export type ShelfType = 'SIMPLE' | 'COMPLEX' | 'PANTRY';

export type Panel = {
    ID: string;
    OpID: string;
    MatID: string;
    LabelID: string;
    Name: string;
    Quantity: number;
    Width: number;
    Length: number;
    Thickness: number;
    Description: string;
    Routing: string;
    Class: string;
    Type: string;
    Grain: string;
};

type Item = {
    ID: string;
    JobID: string;
    Parts: Record<string, Panel>;
};

type Manufacturing = {
    ID: string;
    JobID: string;
    OperationGroups: Record<string, OperationGroup>;
};

type Job = {
    ID: string;
    Name: string;
    Source: string;
    Items: Record<string, Item>;
    Manufacturing: Record<string, Manufacturing>;
};

export type Batch = {
    Jobs: Record<string, Job>;
};

export type Edge = {l1: boolean; l2: boolean; w1: boolean; w2: boolean};

export type ConditionalValues = ConditionalRender & {
    key: string;
};

export type ConditionalRender = {
    condition: boolean;
    callback: () => void;
};

export type HoleConfig = {
    holeRadius: number;
    holeSpacing: number;
    holeOffset: number;
    backsideOffset: number;
    bottomHoleOffset: number;
    holeDepth: number;
};

type Drillings = {
    drilling_num_holes: number;
    drilling_offset_x: number;
    drilling_offset_y: number;
    drilling_pitch: number;
};

export type PreviewFormValues = {
    [key: string]:
        | number
        | boolean
        | string
        | number[]
        | Drawer[]
        | AdvancedUpperShelf[]
        | AdvancedShelf[]
        | AdvancedMiddleShelf[]
        | AdvancedLowerShelf[]
        | {image?: string}
        | Drillings[];
    cabinet_height?: number;
    cabinet_depth?: number;
    cabinet_width?: number;
    cabinet_door_gap?: number;
    cabinet_door_top?: number;
    cabinet_door_bottom?: number;
    cabinet_door_left?: number;
    cabinet_door_right?: number;
    cabinet_drawer_top?: number;
    cabinet_return_panel_width?: number;
    cabinet_void_width?: number;
    cabinet_cover_void?: boolean;
    cabinet_left_width?: number;
    cabinet_right_depth?: number;
    cabinet_right_width?: number;
    cabinet_left_depth?: number;
    drawers?: Drawer[];
    cabinet_drawer_gap?: number;
    cabinet_drawer_bottom?: number;
    cabinet_drawer_left?: number;
    cabinet_drawer_right?: number;
    oven_opening_height?: number;
    microwave_opening_height?: number;
    cabinet_partition_height?: number;
    drawer_face_height?: number[];
    cabinet_toekick?: number;
    cabinet_panel_length?: number;
    cabinet_panel_width?: number;
    cabinet_quantity?: number;
    shelves?: AdvancedShelf[];
    shelf_set_back?: number;
    upper_shelves?: AdvancedUpperShelf[];
    middle_shelves?: AdvancedMiddleShelf[];
    lower_shelves?: AdvancedLowerShelf[];
    simple_shelves_amount?: number;
    cabinet_vert_shelves?: number;
    cabinet_hori_shelves?: number;
    cabinet_partition_width?: number;
    cabinet_ladder_frames?: number;
    cabinet_simple_shelves?: number;
    hor_grain_ext?: boolean;
    hor_grain_carc?: boolean;
    cabinet_edge_L1?: boolean | string;
    cabinet_edge_l1?: boolean | string;
    panel_edge_top?: boolean | string;
    panel_edge_left?: boolean | string;
    panel_edge_right?: boolean | string;
    cabinet_edge_L2?: boolean | string;
    cabinet_edge_l2?: boolean | string;
    cabinet_edge_W1?: boolean | string;
    cabinet_edge_w1?: boolean | string;
    cabinet_edge_W2?: boolean | string;
    cabinet_edge_w2?: boolean | string;
    panel_edge_bottom?: boolean | string;
    cabinet_door?: boolean;
    include_drawer_faces?: boolean;
    cabinet_include_hardware?: boolean;
    cabinet_adjustable_legs?: boolean;
    cabinet_top?: number;
    cabinet_width_depth?: number;
    cabinet_length_depth?: number;
    cabinet_int_radius?: number;
    cabinet_ext_radius?: number;
    simple_shelf_type?: number;
    rangehood_opening_height?: number;
    cabinet_length1?: number;
    cabinet_length2?: number;
    cabinet_width1?: number;
    cabinet_upper_partition_height?: number;
    cabinet_lower_partition_height?: number;
    cabinet_total_drawer_height?: number;
    job_cabinet_id?: number;
    type?: number;
    cabinet_carc_thickness?: number;
    cabinet_ext_thickness?: number;
    cabinet_carc_image?: string;
    cabinet_carc_edge_image?: string;
    cabinet_ext_image?: string;
    cabinet_ext_edge_image?: string;
    carc_material?: {image?: string};
    carc_edge?: {image?: string};
    ext_material?: {image?: string; brand_name?: string};
    ext_edge?: {image?: string};
    isSwitchingCabinet?: boolean;
    fromBackup?: boolean;
    isSavingProduct?: boolean;
    drillings?: Drillings[];
    cabinet_type: number;
    vert_mid_rail_positions?: number[];
    hori_mid_rail_positions?: number[];
    drawer_runner_specs?: string;
    border_width_top?: number;
    border_width_left?: number;
    border_width_bottom?: number;
    border_width_right?: number;
    hori_height?: number;
    vert_width?: number;
    drawer_border_width_top?: number;
    drawer_border_width_left?: number;
    drawer_border_width_bottom?: number;
    drawer_border_width_right?: number;
    drawer_face_type?: number;
    drawer_amount?: number;
    isInnerDrawer?: boolean;
    fromUndoOrRedo?: boolean;
    sideEffectFromUpdate?: boolean;
    simple_shelves_thickness?: number;
    _isShelfTypeUpdate?: boolean;
};

export type Drawer = {
    drawer_face_height: number;
    drawer_runner_specs: string;
    drawer_type: number;
    drawer_runner_height?: number;
    drawer_runner_depth?: number;
    drawer_runner_desc?: string;
    drawer_style?: number;
    position_to_bottom?: number;
};

export type AdvancedShelf = {
    shelf_type: number;
    shelf_style: number;
    shelf_position: number;
    shelf_offset: number;
    insert_id?: number;
};

export type AdvancedUpperShelf = {
    upper_shelf_type: number;
    upper_shelf_style: number;
    upper_shelf_position: number;
    upper_shelf_offset: number;
};

export type AdvancedLowerShelf = {
    lower_shelf_type: number;
    lower_shelf_style: number;
    lower_shelf_position: number;
    lower_shelf_offset: number;
};

export type AdvancedMiddleShelf = {
    middle_shelf_type: number;
    middle_shelf_style: number;
    middle_shelf_position: number;
    middle_shelf_offset: number;
};

export type PreviewLocation = 'DOORS' | 'SHELVES' | 'SPECS' | 'DRAWERS';

export type SlugQueryParams = {
    slug: string;
};

export type LegsOffset = {
    front?: number;
    back?: number;
    topLeft?: number;
    topRight?: number;
    bottomRight?: number;
    bottomLeft?: number;
};

export type LShapeVariant = 'base' | 'upper' | '3door' | 'upper3door';

export const isBaseCornerCabinet = (
    variables: TemplateVariable | undefined,
    values: {upper_shelves?: unknown} | undefined
): boolean =>
    Boolean(variables?.hasCornerShapedShelves) &&
    !values?.upper_shelves &&
    !variables?.isReturnProduct;

// Corner pantry (tall corner) counterpart of a base corner: it carries
// upper_shelves so isBaseCornerCabinet is false for it, but its corner "hang"
// doors need the same left/right reveal correction. Mirrors the isCornerPantry
// guard in generateCabinetMesh (covers both the 2-part and 3-part variants).
export const isCornerPantryCabinet = (
    variables: TemplateVariable | undefined,
    values: {upper_shelves?: unknown; shelves?: unknown} | undefined
): boolean =>
    Boolean(variables?.isCornerProduct) &&
    Boolean(values?.upper_shelves) &&
    !values?.shelves &&
    !variables?.isReturnProduct;

export const detectLShapeVariant = (
    templateName: string | undefined,
    variables: TemplateVariable
): LShapeVariant | undefined => {
    if (typeof templateName === 'string' && /LShape3Door/.test(templateName)) {
        return variables?.isUpperProduct ? 'upper3door' : '3door';
    }
    const isLShape = hasLShapeFootprint(variables);
    if (!isLShape) return undefined;
    if (variables?.isUpperProduct) return 'upper';
    return 'base';
};

export type LShapeDoorTuning = {
    perimeterAnchor: number;
    depthGap: number;
    cornerGap: number;
    openCompensationFactor: number;
    leftSmallInsertYAdjust: number;
};

export type NormalizeLShapeInputs = {
    cabinetLeftDepth: number;
    cabinetRightDepth: number;
    cabinetLeftWidth: number;
    cabinetRightWidth: number;
    cabinetHeight: number;
    doorGap: number;
    doorTop: number;
    doorBottom: number;
    doorLeft: number;
    doorRight: number;
    doorThickness?: number;
    doorTuning?: Partial<LShapeDoorTuning>;
    carc: number;
    isBaseCorner?: boolean;
    isCornerPantry?: boolean;
};

export const resolveDoorThickness = (inputs: NormalizeLShapeInputs): number => {
    const {doorThickness} = inputs;
    return doorThickness !== undefined && Number.isFinite(doorThickness)
        ? doorThickness
        : inputs.carc;
};
